import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, CalendarDays, ClipboardList, Pencil, Plus, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DISCOVERY_API, OWNING_ROLES, PRIORITIES, SESSION_KINDS, SESSION_STATUSES,
  canAccessDiscovery, discoveryErrorMessage, discoveryQueryKey, formatSchedule,
  isDiscoverySchemaPending, localScheduleToUtc, sessionFormSchema, useCaseFormSchema, utcScheduleToLocal,
  type DiscoverySession, type DiscoveryUseCase, type DiscoveryWorkspace,
  type SessionFormValues, type SessionValues, type UseCaseValues,
} from "./discovery-workspace.helpers";

type Editor = { type: "session"; item?: DiscoverySession } | { type: "use-case"; item?: DiscoveryUseCase };
type DeleteTarget = { resource: "sessions" | "use-cases"; id: string; title: string };
type WriteRequest =
  | { method: "POST" | "PUT"; resource: "sessions"; id?: string; data: SessionValues }
  | { method: "POST" | "PUT"; resource: "use-cases"; id?: string; data: UseCaseValues }
  | { method: "DELETE"; resource: "sessions" | "use-cases"; id: string };

const label = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function ActionError({ error }: { error: unknown }) {
  if (!error) return null;
  return <p role="alert" className="text-sm text-destructive" data-testid="status-discovery-action-error">{discoveryErrorMessage(error)}</p>;
}

interface FormProps {
  pending: boolean;
  disabled: boolean;
  error: unknown;
  onCancel: () => void;
}

function SessionForm({ item, pending, disabled, error, onCancel, onSave }: FormProps & {
  item?: DiscoverySession;
  onSave: (values: SessionValues) => void;
}) {
  const form = useForm<SessionFormValues>({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: {
      title: item?.title ?? "", kind: item?.kind ?? "interview",
      scheduledAt: utcScheduleToLocal(item?.scheduledAt ?? null),
      notes: item?.notes ?? "", status: item?.status ?? "scheduled",
    },
  });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return <Form {...form}>
    <form className="space-y-4" onSubmit={form.handleSubmit(values => onSave({ ...values, scheduledAt: localScheduleToUtc(values.scheduledAt) }))}>
      <fieldset disabled={pending || disabled} className="space-y-4">
        <FormField control={form.control} name="title" render={({ field }) => <FormItem>
          <FormLabel>Session title *</FormLabel>
          <FormControl><Input {...field} maxLength={200} data-testid="input-session-title" /></FormControl>
          <FormMessage />
        </FormItem>} />
        <div className="grid gap-4 md:grid-cols-2">
          <FormField control={form.control} name="kind" render={({ field }) => <FormItem>
            <FormLabel>Session type *</FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={pending || disabled}>
              <FormControl><SelectTrigger data-testid="select-session-kind"><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>{SESSION_KINDS.map(value => <SelectItem key={value} value={value} data-testid={`option-session-kind-${value}`}>{label(value)}</SelectItem>)}</SelectContent>
            </Select><FormMessage />
          </FormItem>} />
          <FormField control={form.control} name="status" render={({ field }) => <FormItem>
            <FormLabel>Status *</FormLabel>
            <Select value={field.value} onValueChange={field.onChange} disabled={pending || disabled}>
              <FormControl><SelectTrigger data-testid="select-session-status"><SelectValue /></SelectTrigger></FormControl>
              <SelectContent>{SESSION_STATUSES.map(value => <SelectItem key={value} value={value} data-testid={`option-session-status-${value}`}>{label(value)}</SelectItem>)}</SelectContent>
            </Select><FormMessage />
          </FormItem>} />
        </div>
        <FormField control={form.control} name="scheduledAt" render={({ field }) => <FormItem>
          <FormLabel>Date and time</FormLabel>
          <FormControl><Input {...field} type="datetime-local" step="1" data-testid="input-session-scheduled-at" /></FormControl>
          <FormDescription>Local time ({timezone}). Saved in UTC. Leave blank until a time is agreed.</FormDescription>
          <FormMessage />
        </FormItem>} />
        <FormField control={form.control} name="notes" render={({ field }) => <FormItem>
          <FormLabel>Notes</FormLabel>
          <FormControl><Textarea {...field} rows={5} maxLength={10000} placeholder="Stakeholders, agenda, and findings" data-testid="input-session-notes" /></FormControl>
          <FormDescription>Up to 10,000 characters.</FormDescription><FormMessage />
        </FormItem>} />
      </fieldset>
      <ActionError error={error} />
      {disabled && <p className="text-sm text-muted-foreground" role="status">Saving is disabled until the workspace is available.</p>}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={pending} onClick={onCancel} data-testid="button-cancel-session">Cancel</Button>
        <Button type="submit" disabled={pending || disabled} data-testid="button-save-session">{pending ? "Saving…" : item ? "Save changes" : "Create session"}</Button>
      </DialogFooter>
    </form>
  </Form>;
}

function UseCaseForm({ item, pending, disabled, error, onCancel, onSave }: FormProps & {
  item?: DiscoveryUseCase;
  onSave: (values: UseCaseValues) => void;
}) {
  const form = useForm<UseCaseValues>({
    resolver: zodResolver(useCaseFormSchema),
    defaultValues: { title: item?.title ?? "", description: item?.description ?? "", owningRole: item?.owningRole, priority: item?.priority ?? "medium" },
  });
  return <Form {...form}>
    <form className="space-y-4" onSubmit={form.handleSubmit(onSave)}>
      <fieldset disabled={pending || disabled} className="space-y-4">
        <FormField control={form.control} name="title" render={({ field }) => <FormItem>
          <FormLabel>Use case title *</FormLabel>
          <FormControl><Input {...field} maxLength={200} data-testid="input-use-case-title" /></FormControl><FormMessage />
        </FormItem>} />
        <FormField control={form.control} name="owningRole" render={({ field }) => <FormItem>
          <FormLabel>Owning role *</FormLabel>
          <Select value={field.value ?? ""} onValueChange={field.onChange} disabled={pending || disabled}>
            <FormControl><SelectTrigger data-testid="select-use-case-owning-role"><SelectValue placeholder="Choose one owning role" /></SelectTrigger></FormControl>
            <SelectContent>{OWNING_ROLES.map((value, index) => <SelectItem key={value} value={value} data-testid={`option-owning-role-${index}`}>{value}</SelectItem>)}</SelectContent>
          </Select>
          <FormDescription>Exactly one owner is required. These categories do not change application permissions.</FormDescription><FormMessage />
        </FormItem>} />
        <FormField control={form.control} name="priority" render={({ field }) => <FormItem>
          <FormLabel>Priority *</FormLabel>
          <Select value={field.value} onValueChange={field.onChange} disabled={pending || disabled}>
            <FormControl><SelectTrigger data-testid="select-use-case-priority"><SelectValue /></SelectTrigger></FormControl>
            <SelectContent>{PRIORITIES.map(value => <SelectItem key={value} value={value} data-testid={`option-use-case-priority-${value}`}>{label(value)}</SelectItem>)}</SelectContent>
          </Select><FormMessage />
        </FormItem>} />
        <FormField control={form.control} name="description" render={({ field }) => <FormItem>
          <FormLabel>Description</FormLabel>
          <FormControl><Textarea {...field} rows={6} maxLength={10000} placeholder="Describe the need, expected outcome, and acceptance criteria" data-testid="input-use-case-description" /></FormControl>
          <FormDescription>Up to 10,000 characters.</FormDescription><FormMessage />
        </FormItem>} />
      </fieldset>
      <ActionError error={error} />
      {disabled && <p className="text-sm text-muted-foreground" role="status">Saving is disabled until the workspace is available.</p>}
      <DialogFooter>
        <Button type="button" variant="outline" disabled={pending} onClick={onCancel} data-testid="button-cancel-use-case">Cancel</Button>
        <Button type="submit" disabled={pending || disabled} data-testid="button-save-use-case">{pending ? "Saving…" : item ? "Save changes" : "Create use case"}</Button>
      </DialogFooter>
    </form>
  </Form>;
}

function Workspace({ organizationId, organizationName, userId }: { organizationId: string; organizationName: string; userId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editor, setEditor] = useState<Editor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const queryKey = discoveryQueryKey(organizationId, userId);
  const query = useQuery<DiscoveryWorkspace>({
    queryKey,
    queryFn: async () => (await apiRequest("GET", DISCOVERY_API)).json(),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 60000,
  });
  const write = useMutation({
    mutationFn: async (request: WriteRequest) => {
      const url = `${DISCOVERY_API}/${request.resource}${request.id ? `/${encodeURIComponent(request.id)}` : ""}`;
      const response = await apiRequest(request.method, url, request.method === "DELETE" ? undefined : request.data);
      return request.method === "DELETE" ? undefined : response.json();
    },
    onSuccess: async (_record, request) => {
      setEditor(null);
      setDeleteTarget(null);
      toast({ title: request.method === "DELETE" ? "Record deleted" : request.method === "POST" ? "Record created" : "Changes saved" });
      await qc.invalidateQueries({ queryKey });
    },
    onError: () => {
      // Recheck availability after a failed write, including a migration gate
      // activated after the initial GET. Never create client-only records.
      void qc.invalidateQueries({ queryKey });
    },
  });
  const disabled = query.isPending || query.isError || !query.data
    || (write.isError && query.isFetching) || isDiscoverySchemaPending(write.error);
  const pending = write.isPending;
  const sessions = query.data?.sessions ?? [];
  const useCases = query.data?.useCases ?? [];
  const openEditor = (next: Editor) => { write.reset(); setEditor(next); };
  const openDelete = (next: DeleteTarget) => { write.reset(); setDeleteTarget(next); };
  const closeEditor = () => { if (!pending) { setEditor(null); write.reset(); } };

  return <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
    <div>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-discovery-title">Discovery &amp; Requirements</h1>
      <p className="mt-1 text-sm text-muted-foreground">Schedule stakeholder conversations and capture use cases with a clear owner.</p>
      <p className="mt-2 text-xs text-muted-foreground" data-testid="text-discovery-organization">Organization: <span className="font-medium text-foreground">{organizationName}</span>. All records are scoped to this organization.</p>
    </div>

    {(query.isError || isDiscoverySchemaPending(write.error)) && <Card>
      <CardContent className="p-6 flex items-start gap-3" role="alert" data-testid="status-discovery-unavailable">
        <AlertCircle className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
        <div className="space-y-2">
          <h2 className="font-semibold">{isDiscoverySchemaPending(query.error) || isDiscoverySchemaPending(write.error) ? "Awaiting migration approval" : "Discovery workspace unavailable"}</h2>
          <p className="text-sm text-muted-foreground">{discoveryErrorMessage(query.error ?? write.error)}</p>
          <p className="text-sm text-muted-foreground">If database setup is awaiting migration approval, this workspace will remain disabled until approval and deployment are complete. No changes can be saved while the workspace is unavailable.</p>
          <Button variant="outline" size="sm" disabled={query.isFetching || pending} onClick={() => { write.reset(); void query.refetch(); }} data-testid="button-retry-discovery">{query.isFetching ? "Checking availability…" : "Retry"}</Button>
        </div>
      </CardContent>
    </Card>}

    <Tabs defaultValue="sessions" className="space-y-4">
      <TabsList className="h-auto flex-wrap">
        <TabsTrigger value="sessions" data-testid="tab-discovery-sessions"><CalendarDays className="h-4 w-4 mr-2" aria-hidden="true" />Sessions{!disabled && <span className="ml-2 text-xs">({sessions.length})</span>}</TabsTrigger>
        <TabsTrigger value="use-cases" data-testid="tab-discovery-use-cases"><ClipboardList className="h-4 w-4 mr-2" aria-hidden="true" />Use cases{!disabled && <span className="ml-2 text-xs">({useCases.length})</span>}</TabsTrigger>
      </TabsList>
      <TabsContent value="sessions">
        <Card>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div><CardTitle className="text-lg">Interviews &amp; workshops</CardTitle><p className="text-sm text-muted-foreground mt-1">Plan sessions, record findings, and track completion.</p></div>
            <Button disabled={disabled || pending} onClick={() => openEditor({ type: "session" })} data-testid="button-add-discovery-session"><Plus className="h-4 w-4 mr-2" aria-hidden="true" />Add session</Button>
          </CardHeader>
          <CardContent>
            {query.isPending ? <WorkspaceSkeleton /> : query.isError ? <p className="text-sm text-muted-foreground">Sessions cannot be displayed until the workspace is available.</p> : sessions.length === 0 ? <EmptyPanel kind="session" /> : <div className="divide-y">
              {sessions.map(session => <article key={session.id} className="py-4 first:pt-0 last:pb-0" data-testid={`row-discovery-session-${session.id}`}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 space-y-2">
                    <h3 className="font-medium break-words" data-testid={`text-session-title-${session.id}`}>{session.title}</h3>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline">{label(session.kind)}</Badge>
                      <Badge variant={session.status === "completed" ? "default" : "secondary"} data-testid={`status-session-${session.id}`}>{label(session.status)}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground" data-testid={`text-session-schedule-${session.id}`}>{formatSchedule(session.scheduledAt)}{session.scheduledAt && " (local time)"}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" disabled={disabled || pending} onClick={() => openEditor({ type: "session", item: session })} aria-label={`Edit session: ${session.title}`} data-testid={`button-edit-session-${session.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Edit</Button>
                    <Button variant="ghost" size="sm" className="text-destructive" disabled={disabled || pending} onClick={() => openDelete({ resource: "sessions", id: session.id, title: session.title })} aria-label={`Delete session: ${session.title}`} data-testid={`button-delete-session-${session.id}`}><Trash2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Delete</Button>
                  </div>
                </div>
                {session.notes && <p className="mt-3 whitespace-pre-wrap break-words text-sm text-muted-foreground" data-testid={`text-session-notes-${session.id}`}>{session.notes}</p>}
              </article>)}
            </div>}
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="use-cases">
        <Card>
          <CardHeader className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div><CardTitle className="text-lg">Owned use cases</CardTitle><p className="text-sm text-muted-foreground mt-1">Every use case belongs to exactly one owning role.</p></div>
            <Button disabled={disabled || pending} onClick={() => openEditor({ type: "use-case" })} data-testid="button-add-discovery-use-case"><Plus className="h-4 w-4 mr-2" aria-hidden="true" />Add use case</Button>
          </CardHeader>
          <CardContent>
            {query.isPending ? <WorkspaceSkeleton /> : query.isError ? <p className="text-sm text-muted-foreground">Use cases cannot be displayed until the workspace is available.</p> : useCases.length === 0 ? <EmptyPanel kind="use case" /> : <div className="divide-y">
              {useCases.map(useCase => <article key={useCase.id} className="py-4 first:pt-0 last:pb-0" data-testid={`row-discovery-use-case-${useCase.id}`}>
                <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0 space-y-2">
                    <h3 className="font-medium break-words" data-testid={`text-use-case-title-${useCase.id}`}>{useCase.title}</h3>
                    <div className="flex flex-wrap gap-2"><Badge variant="outline" data-testid={`text-use-case-owner-${useCase.id}`}>{useCase.owningRole}</Badge><Badge variant={useCase.priority === "high" ? "default" : "secondary"} data-testid={`text-use-case-priority-${useCase.id}`}>{label(useCase.priority)} priority</Badge></div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="outline" size="sm" disabled={disabled || pending} onClick={() => openEditor({ type: "use-case", item: useCase })} aria-label={`Edit use case: ${useCase.title}`} data-testid={`button-edit-use-case-${useCase.id}`}><Pencil className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Edit</Button>
                    <Button variant="ghost" size="sm" className="text-destructive" disabled={disabled || pending} onClick={() => openDelete({ resource: "use-cases", id: useCase.id, title: useCase.title })} aria-label={`Delete use case: ${useCase.title}`} data-testid={`button-delete-use-case-${useCase.id}`}><Trash2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Delete</Button>
                  </div>
                </div>
                {useCase.description && <p className="mt-3 whitespace-pre-wrap break-words text-sm text-muted-foreground" data-testid={`text-use-case-description-${useCase.id}`}>{useCase.description}</p>}
              </article>)}
            </div>}
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>

    <Dialog open={!!editor} onOpenChange={open => { if (!open) closeEditor(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90dvh] overflow-y-auto" onEscapeKeyDown={event => { if (pending) event.preventDefault(); }} onInteractOutside={event => { if (pending) event.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{editor?.item ? "Edit" : "Create"} {editor?.type === "session" ? "session" : "use case"}</DialogTitle>
          <DialogDescription>Saved to {organizationName}. Fields marked * are required.</DialogDescription>
        </DialogHeader>
        {editor?.type === "session" && <SessionForm key={`session-${editor.item?.id ?? "new"}`} item={editor.item} pending={pending} disabled={disabled} error={write.error} onCancel={closeEditor}
          onSave={data => { if (!disabled && !pending) write.mutate({ method: editor.item ? "PUT" : "POST", resource: "sessions", id: editor.item?.id, data }); }} />}
        {editor?.type === "use-case" && <UseCaseForm key={`use-case-${editor.item?.id ?? "new"}`} item={editor.item} pending={pending} disabled={disabled} error={write.error} onCancel={closeEditor}
          onSave={data => { if (!disabled && !pending) write.mutate({ method: editor.item ? "PUT" : "POST", resource: "use-cases", id: editor.item?.id, data }); }} />}
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!deleteTarget} onOpenChange={open => { if (!open && !pending) { setDeleteTarget(null); write.reset(); } }}>
      <AlertDialogContent onEscapeKeyDown={event => { if (pending) event.preventDefault(); }}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {deleteTarget?.resource === "sessions" ? "session" : "use case"}?</AlertDialogTitle>
          <AlertDialogDescription>“{deleteTarget?.title}” will be permanently deleted from {organizationName}. This cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <ActionError error={write.error} />
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending} data-testid="button-cancel-discovery-delete">Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={disabled || pending} onClick={() => { if (deleteTarget && !disabled && !pending) write.mutate({ method: "DELETE", ...deleteTarget }); }} data-testid="button-confirm-discovery-delete">{pending ? "Deleting…" : "Delete permanently"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;
}

function WorkspaceSkeleton() {
  return <div className="space-y-4" role="status" aria-label="Loading discovery workspace" data-testid="status-discovery-loading">
    {[0, 1, 2].map(index => <div className="space-y-2 py-2" key={index}><Skeleton className="h-5 w-2/3" /><Skeleton className="h-4 w-1/3" /><Skeleton className="h-4 w-5/6" /></div>)}
  </div>;
}

function EmptyPanel({ kind }: { kind: "session" | "use case" }) {
  const Icon = kind === "session" ? CalendarDays : ClipboardList;
  return <div className="py-10 text-center space-y-2" data-testid={`status-discovery-empty-${kind.replace(" ", "-")}`}>
    <Icon className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
    <h3 className="text-sm font-medium">No {kind === "session" ? "sessions" : "use cases"} yet</h3>
    <p className="text-sm text-muted-foreground">{kind === "session" ? "Add an interview or workshop to start gathering requirements." : "Capture a requirement and assign its owning role to get started."}</p>
  </div>;
}

export default function DiscoveryWorkspacePage() {
  const { user, organization } = useAuth();
  if (!canAccessDiscovery(user?.role)) {
    return <div className="p-6"><h1 className="text-xl font-semibold">Access restricted</h1><p className="mt-2 text-sm text-muted-foreground">Discovery &amp; Requirements is available to organization and super administrators only.</p></div>;
  }
  // The server derives organization scope from the caller. Never send an
  // organization selector or attempt a cross-tenant super-admin request.
  if (!organization?.id || !user?.id) {
    return <div className="p-4 md:p-6 max-w-6xl mx-auto"><h1 className="text-2xl font-semibold">Discovery &amp; Requirements</h1><Card className="mt-6"><CardContent className="p-6" role="status" data-testid="status-discovery-no-organization"><h2 className="font-semibold">Organization context required</h2><p className="mt-2 text-sm text-muted-foreground">An organization-scoped administrator session is required, including for super administrators. No workspace data is requested or saved without an organization.</p></CardContent></Card></div>;
  }
  return <Workspace key={`${organization.id}:${user.id}`} organizationId={organization.id} organizationName={organization.name || "your organization"} userId={user.id} />;
}
